import React from "react";
import { CheckCircle2 } from "lucide-react";

const PurchasesPlans = () => {
  const activeFeatures = [
    "Personalized weekly meal plans",
    "4× 1:1 Video Consultations/mo",
    "Unlimited chat support",
    "Advanced biometric tracking",
  ];

  const plans = [
    {
      name: "Starter",
      price: "$29",
      sub: "Essential tools to jumpstart your health journey.",
      btn: "Downgrade to Starter",
      active: false,
      green: false,
      features: [
        "Basic meal templates",
        "Calorie & macro tracking",
        "Community forum access",
        "1 Monthly group Q&A",
      ],
    },
    {
      name: "Premium Wellness",
      price: "$99",
      sub: "Comprehensive support with 1:1 coaching & guidance.",
      btn: "Active Plan",
      active: true,
      green: false,
      features: [
        "Personalized weekly meal plans",
        "4× 1:1 Video Consultations/mo",
        "Unlimited chat support",
        "Advanced biometric tracking",
      ],
    },
    {
      name: "Elite Care",
      price: "$199",
      sub: "The ultimate personalized health concierge experience.",
      btn: "Upgrade to Elite",
      active: false,
      green: true,
      features: [
        "Everything in Premium",
        "Daily menu adjustments",
        "Custom fitness & workout plans",
        "Free monthly supplement box",
      ],
    },
  ];

  return (
    <div className="w-full font-lato">
      <div className="mb-7">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight">
          Purchases & Plans
        </h2>
        <p className="text-[#98a19b] text-sm mt-2">
          Manage your active subscriptions, view plan benefits, and explore new
          packages.
        </p>
      </div>
      <h3 className="text-xl sm:text-xl font-semibold text-[#24312d] mb-4">
        Active Subscription
      </h3>

      <div className="bg-white border border-[#e8ece8] rounded-xl p-6 mb-8">
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-6">
          <div>
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-[#58b96b] text-white text-[11px] font-medium">
                Active
              </span>
              <span className="text-[12px] text-[#98a19b]">
                Member since Mar 2023
              </span>
            </div>

            <h4 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-tight mb-3">
              Premium Wellness Plan
            </h4>

            <p className="text-[14px] text-[#98a19b] leading-6 max-w-[640px] mb-6">
              Comprehensive nutrition and health management with weekly
              specialist consultations, personalized meal tracking, and priority
              support.
            </p>

            <p className="text-[14px] font-semibold text-[#24312d] mb-4">
              Plan Includes:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeFeatures.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-[#58b96b]" />
                  <span className="text-[14px] text-[#24312d]">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-[#edf1ed] rounded-xl p-5 h-fit">
            <div className="mb-6">
              <h4 className="text-2xl sm:text-3xl font-semibold text-[#24312d] leading-none">
                $99
                <span className="text-[15px] font-normal text-[#98a19b]">
                  /mo
                </span>
              </h4>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between gap-4">
                <span className="text-[13px] text-[#98a19b]">
                  Billing Cycle
                </span>
                <span className="text-[13px] font-medium text-[#24312d]">
                  Monthly
                </span>
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-[13px] text-[#98a19b]">
                  Next Payment
                </span>
                <span className="text-[13px] font-medium text-[#24312d]">
                  Nov 15, 2024
                </span>
              </div>

              <div className="flex justify-between gap-4">
                <span className="text-[13px] text-[#98a19b]">
                  Payment Method
                </span>
                <span className="text-[13px] font-medium text-[#24312d]">
                  •• 4242
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button className="h-[40px] rounded-md bg-[#58b96b] hover:bg-[#49aa5d] text-white text-[13px] font-medium">
                Change Plan
              </button>

              <button className="h-[40px] rounded-md border border-[#dfe5df] text-[13px] text-[#7f8882] hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <h3 className="text-xl sm:text-xl font-semibold text-[#24312d]">
          Available Plans
        </h3>

        <button className="h-[36px] px-4 rounded-md border border-[#dfe5df] text-[12px] text-[#5f6964] hover:bg-gray-50 w-fit">
          Compare Features
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {plans.map((plan, index) => (
          <div
            key={index}
            className={`bg-white rounded-xl p-6 border relative ${
              plan.active
                ? "border-[#58b96b] shadow-sm"
                : "border-[#e8ece8]"
            }`}
          >
            {plan.active && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#58b96b] text-white text-[11px] font-medium">
                Current Plan
              </span>
            )}

            <div className="text-center mb-6">
              <h4 className="text-xl sm:text-xl font-semibold text-[#24312d] mb-2">
                {plan.name}
              </h4>

              <p className="text-[12px] text-[#98a19b] leading-6 min-h-[48px]">
                {plan.sub}
              </p>

              <div className="mt-1">
                <span className="text-2xl sm:text-2xl font-semibold text-[#24312d] leading-none">
                  {plan.price}
                </span>
                <span className="text-[15px] text-[#98a19b]">/mo</span>
              </div>
            </div>

            <div className="border-t border-[#edf1ed] pt-5 space-y-4 mb-6">
              {plan.features.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={15} className="text-[#58b96b]" />
                  <span className="text-[14px] text-[#24312d]">{item}</span>
                </div>
              ))}
            </div>

            <button
              className={`w-full h-[42px] rounded-md text-[13px] font-medium ${
                plan.green
                  ? "bg-[#58b96b]  text-white"
                  : plan.active
                  ? "bg-[#f4faf5] text-[#58b96b]"
                  : "border border-[#dfe5df] text-[#7f8882] hover:bg-gray-50"
              }`}
            >
              {plan.btn}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PurchasesPlans;